<template>
  <Dialog
    v-model:visible="visibleProxy"
    modal
    append-to="body"
    :closable="false"
    :draggable="false"
    class="decision-form-dialog w-full"
    :pt="dialogPt"
    data-testid="decision-form-dialog"
    @show="handleShow"
    @hide="handleHide"
  >
    <template #header>
      <div class="decision-form-dialog__header">
        <div class="decision-form-dialog__header-text">
          <h2 id="decision-form-dialog-title" class="decision-form-dialog__title">
            {{ dialogTitle }}
          </h2>
          <p v-if="projectName" class="decision-form-dialog__subtitle">
            {{ projectName }}
          </p>
        </div>
        <button
          type="button"
          class="decision-form-dialog__close"
          :aria-label="$t('ProjectManagement.cancel')"
          data-testid="decision-form-close"
          @click="close"
        >
          <i class="pi pi-times" aria-hidden="true" />
        </button>
      </div>
    </template>

    <div class="decision-form-dialog__body">
      <div class="decision-form-dialog__field">
        <label class="decision-form-dialog__label" for="decision-form-title">
          {{ $t('UserProjects.decisionDialog.titleLabel') }}
          <span class="decision-form-dialog__required">*</span>
        </label>
        <p class="decision-form-dialog__hint">{{ $t('UserProjects.decisionDialog.titleHint') }}</p>
        <InputText
          id="decision-form-title"
          v-model="form.title"
          class="decision-form-dialog__input w-full"
          :placeholder="$t('UserProjects.decisionDialog.titlePlaceholder')"
          :invalid="!!formErrors.title"
          data-testid="decision-form-title"
        />
        <small v-if="formErrors.title" class="decision-form-dialog__error">
          {{ formErrors.title }}
        </small>
      </div>

      <div class="decision-form-dialog__row">
        <div class="decision-form-dialog__field">
          <label class="decision-form-dialog__label">
            {{ $t('UserProjects.decisionDialog.deciderLabel') }}
            <span class="decision-form-dialog__required">*</span>
          </label>
          <p class="decision-form-dialog__hint">{{ $t('UserProjects.decisionDialog.deciderHint') }}</p>
          <div class="decision-form-dialog__select-wrap">
            <UserSelectInput
              v-model="form.decisionOwnerUserId"
              :users="participantUsers"
              :invalid="!!formErrors.decisionOwnerUserId"
              data-testid="decision-form-decider"
            />
          </div>
          <small v-if="formErrors.decisionOwnerUserId" class="decision-form-dialog__error">
            {{ formErrors.decisionOwnerUserId }}
          </small>
        </div>

        <div class="decision-form-dialog__field">
          <label class="decision-form-dialog__label">
            {{ $t('UserProjects.decisionDialog.preparerLabel') }}
          </label>
          <p class="decision-form-dialog__hint">{{ $t('UserProjects.decisionDialog.preparerHint') }}</p>
          <div class="decision-form-dialog__select-wrap">
            <UserSelectInput
              v-model="form.preparedByUserId"
              :users="participantUsers"
              data-testid="decision-form-preparer"
            />
          </div>
        </div>
      </div>

      <div class="decision-form-dialog__field">
        <label class="decision-form-dialog__label" for="decision-form-due-date">
          {{ $t('UserProjects.decisionDialog.dueDateLabel') }}
          <span class="decision-form-dialog__required">*</span>
        </label>
        <p class="decision-form-dialog__hint">{{ $t('UserProjects.decisionDialog.dueDateHint') }}</p>
        <InputText
          id="decision-form-due-date"
          v-model="form.dueDate"
          type="date"
          class="decision-form-dialog__input w-full"
          :invalid="!!formErrors.dueDate"
          data-testid="decision-form-due-date"
        />
        <small v-if="formErrors.dueDate" class="decision-form-dialog__error">
          {{ formErrors.dueDate }}
        </small>
      </div>

      <div class="decision-form-dialog__field">
        <label class="decision-form-dialog__label" for="decision-form-basis">
          {{ $t('UserProjects.decisionDialog.basisLabel') }}
        </label>
        <p class="decision-form-dialog__hint">{{ $t('UserProjects.decisionDialog.basisHint') }}</p>
        <Textarea
          id="decision-form-basis"
          v-model="form.basis"
          class="decision-form-dialog__textarea decision-form-dialog__textarea--compact w-full"
          rows="2"
          data-testid="decision-form-basis"
        />
      </div>

      <div class="decision-form-dialog__field">
        <label class="decision-form-dialog__label" for="decision-form-impact">
          {{ $t('UserProjects.decisionDialog.impactLabel') }}
        </label>
        <p class="decision-form-dialog__hint">{{ $t('UserProjects.decisionDialog.impactHint') }}</p>
        <Textarea
          id="decision-form-impact"
          v-model="form.impactIfDelayed"
          class="decision-form-dialog__textarea decision-form-dialog__textarea--compact w-full"
          rows="2"
          data-testid="decision-form-impact"
        />
      </div>

      <hr class="decision-form-dialog__divider" />

      <div class="decision-form-dialog__field">
        <label class="decision-form-dialog__label" for="decision-form-milestone">
          {{ $t('UserProjects.decisionDialog.milestoneLabel') }}
        </label>
        <p class="decision-form-dialog__hint">{{ $t('UserProjects.decisionDialog.milestoneHint') }}</p>
        <Select
          input-id="decision-form-milestone"
          v-model="form.milestoneId"
          :options="milestoneOptions"
          option-label="label"
          option-value="value"
          show-clear
          class="decision-form-dialog__select w-full"
          :placeholder="$t('UserProjects.decisionDialog.milestonePlaceholder')"
          data-testid="decision-form-milestone"
        />
      </div>
    </div>

    <template #footer>
      <div class="decision-form-dialog__footer">
        <button
          type="button"
          class="decision-form-dialog__btn-cancel"
          data-testid="decision-form-cancel"
          @click="close"
        >
          {{ $t('ProjectManagement.cancel') }}
        </button>
        <button
          type="button"
          class="decision-form-dialog__btn-submit"
          :class="{ 'decision-form-dialog__btn-submit--active': canSubmit }"
          :disabled="!canSubmit || saving"
          :data-testid="formMode === 'create' ? 'decision-form-submit-create' : 'decision-form-submit-save'"
          @click="submit"
        >
          <i v-if="saving" class="pi pi-spin pi-spinner" aria-hidden="true" />
          <i v-else class="pi pi-check" aria-hidden="true" />
          <span>{{ submitLabel }}</span>
        </button>
      </div>
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Select from 'primevue/select';
import UserSelectInput from '@/components/global/UserSelectInput.vue';

export const EMPTY_DECISION_FORM = () => ({
  title: '',
  decisionOwnerUserId: '',
  preparedByUserId: '',
  dueDate: '',
  basis: '',
  impactIfDelayed: '',
  milestoneId: null,
});

function toDateInputValue(value) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

export default {
  name: 'DecisionFormDialog',
  components: {
    Dialog,
    InputText,
    Textarea,
    Select,
    UserSelectInput,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    formMode: {
      type: String,
      default: 'create',
    },
    initialDecision: {
      type: Object,
      default: null,
    },
    projectName: {
      type: String,
      default: '',
    },
    participantUsers: {
      type: Array,
      default: () => [],
    },
    milestoneOptions: {
      type: Array,
      default: () => [],
    },
    saving: {
      type: Boolean,
      default: false,
    },
    defaultOwnerUserId: {
      type: String,
      default: '',
    },
  },
  emits: ['update:visible', 'submit', 'cancel'],
  data() {
    return {
      form: EMPTY_DECISION_FORM(),
      formErrors: {},
    };
  },
  computed: {
    visibleProxy: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      },
    },
    dialogTitle() {
      return this.formMode === 'create'
        ? this.$t('UserProjects.decisionDialog.createTitle')
        : this.$t('UserProjects.decisionDialog.editTitle');
    },
    submitLabel() {
      return this.formMode === 'create'
        ? this.$t('UserProjects.decisionDialog.createSubmit')
        : this.$t('UserProjects.decisionDialog.saveSubmit');
    },
    canSubmit() {
      return Boolean(
        String(this.form.title || '').trim()
        && String(this.form.decisionOwnerUserId || '').trim()
        && String(this.form.dueDate || '').trim(),
      );
    },
    dialogPt() {
      return {
        root: {
          'data-testid': 'decision-form-dialog',
          'aria-labelledby': 'decision-form-dialog-title',
        },
      };
    },
  },
  watch: {
    visible(isOpen) {
      if (isOpen) {
        this.resetForm();
      }
    },
  },
  methods: {
    handleShow() {
      this.resetForm();
    },
    handleHide() {
      this.formErrors = {};
      this.$emit('cancel');
    },
    resetForm() {
      if (this.formMode === 'edit' && this.initialDecision) {
        this.form = {
          title: this.initialDecision.title || '',
          decisionOwnerUserId: this.initialDecision.decisionOwnerUserId || '',
          preparedByUserId: this.initialDecision.preparedByUserId || '',
          dueDate: toDateInputValue(this.initialDecision.dueDate),
          basis: this.initialDecision.basis || '',
          impactIfDelayed: this.initialDecision.impactIfDelayed || '',
          milestoneId: this.initialDecision.milestoneId || null,
        };
      } else {
        this.form = {
          ...EMPTY_DECISION_FORM(),
          decisionOwnerUserId: this.defaultOwnerUserId || '',
        };
      }
      this.formErrors = {};
    },
    close() {
      this.visibleProxy = false;
    },
    validateForm() {
      const errors = {};
      if (!String(this.form.title || '').trim()) {
        errors.title = this.$t('ProjectManagement.decisions.validation.titleRequired');
      }
      if (!String(this.form.decisionOwnerUserId || '').trim()) {
        errors.decisionOwnerUserId = this.$t('ProjectManagement.decisions.validation.ownerRequired');
      }
      if (!String(this.form.dueDate || '').trim()) {
        errors.dueDate = this.$t('ProjectManagement.decisions.validation.dueDateRequired');
      }
      this.formErrors = errors;
      return Object.keys(errors).length === 0;
    },
    buildPayload() {
      return {
        title: this.form.title.trim(),
        decisionOwnerUserId: String(this.form.decisionOwnerUserId),
        preparedByUserId: this.form.preparedByUserId ? String(this.form.preparedByUserId) : undefined,
        dueDate: new Date(this.form.dueDate).toISOString(),
        basis: this.form.basis?.trim() || undefined,
        impactIfDelayed: this.form.impactIfDelayed?.trim() || undefined,
        milestoneId: this.form.milestoneId || undefined,
      };
    },
    submit() {
      if (!this.validateForm()) return;
      this.$emit('submit', this.buildPayload());
    },
  },
};
</script>

<style scoped>
.decision-form-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
}

.decision-form-dialog__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.35;
  color: var(--color-dark);
}

.decision-form-dialog__subtitle {
  margin: 0.125rem 0 0;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: var(--color-disabled);
}

.decision-form-dialog__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  margin: -0.25rem -0.25rem 0 0;
  padding: 0;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: var(--color-disabled);
  cursor: pointer;
}

.decision-form-dialog__close:hover {
  color: var(--color-dark);
}

.decision-form-dialog__body {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.decision-form-dialog__field {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.decision-form-dialog__label {
  margin-bottom: 0.25rem;
  font-size: 0.8125rem;
  line-height: 1.4;
  font-weight: 500;
  color: var(--color-disabled);
}

.decision-form-dialog__hint {
  margin: 0 0 0.375rem;
  font-size: 0.75rem;
  line-height: 1.45;
  color: var(--color-inactive-dark);
}

.decision-form-dialog__required {
  color: var(--color-red);
}

.decision-form-dialog__error {
  margin-top: 0.25rem;
  font-size: 0.75rem;
  line-height: 1.4;
  color: var(--color-red);
}

.decision-form-dialog__divider {
  margin: 0;
  border: none;
  border-top: 1px solid var(--border-color, #e5e7eb);
}

.decision-form-dialog__row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

@media (min-width: 640px) {
  .decision-form-dialog__row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.decision-form-dialog__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
}

.decision-form-dialog__btn-cancel {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 0.625rem;
  background: var(--color-white);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
  color: var(--color-dark);
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.decision-form-dialog__btn-cancel:hover {
  background: var(--color-light-grey);
}

.decision-form-dialog__btn-submit {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.625rem;
  background: #d1d5db;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
  color: var(--color-white);
  cursor: not-allowed;
  transition: background 0.15s ease, box-shadow 0.15s ease;
}

.decision-form-dialog__btn-submit--active:not(:disabled) {
  background: var(--accent-gradient);
  cursor: pointer;
}

.decision-form-dialog__btn-submit--active:not(:disabled):hover {
  box-shadow: 0 8px 24px rgba(228, 130, 26, 0.25);
}
</style>

<style>
.decision-form-dialog.p-dialog {
  display: flex;
  flex-direction: column;
  width: min(580px, calc(100vw - 2rem));
  max-height: min(90vh, 52rem);
  margin: 1rem;
  border: none;
  border-radius: 1.25rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  font-family: var(--font-family, 'AsenPro', sans-serif);
}

.decision-form-dialog.p-dialog .p-dialog-header {
  flex-shrink: 0;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
  background: var(--color-white);
}

.decision-form-dialog.p-dialog .p-dialog-content {
  flex: 1 1 auto;
  min-height: 0;
  padding: 1rem 1.5rem 0.75rem;
  background: var(--color-white);
  overflow-x: hidden;
  overflow-y: auto;
}

.decision-form-dialog.p-dialog .p-dialog-footer {
  flex-shrink: 0;
  padding: 0 1.5rem 1rem;
  border-top: 1px solid var(--border-color, #e5e7eb);
  background: var(--color-white);
}

.decision-form-dialog .decision-form-dialog__input.p-inputtext {
  width: 100%;
  min-height: 2.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e5e5;
  border-radius: 0.625rem;
  background: var(--color-white);
  font-size: 0.875rem;
  line-height: 1.4;
  color: var(--color-dark);
  box-shadow: none;
}

.decision-form-dialog .decision-form-dialog__input.p-inputtext:enabled:focus {
  border-color: var(--color-orange);
  box-shadow: 0 0 0 2px rgba(228, 130, 26, 0.15);
  outline: none;
}

.decision-form-dialog .decision-form-dialog__textarea.p-textarea,
.decision-form-dialog .decision-form-dialog__textarea .p-textarea {
  width: 100%;
  min-height: 4.25rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid #e5e5e5;
  border-radius: 0.625rem;
  background: var(--color-white);
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.45;
  color: var(--color-dark);
  resize: vertical;
  box-shadow: none;
}

.decision-form-dialog .decision-form-dialog__textarea.p-textarea:enabled:focus,
.decision-form-dialog .decision-form-dialog__textarea .p-textarea:enabled:focus {
  border-color: var(--color-orange);
  box-shadow: 0 0 0 2px rgba(228, 130, 26, 0.15);
  outline: none;
}

.decision-form-dialog .decision-form-dialog__select.p-select,
.decision-form-dialog .decision-form-dialog__select-wrap .p-select {
  width: 100%;
  min-height: 2.5rem;
  border: 1px solid #e5e5e5;
  border-radius: 0.625rem;
  background: var(--color-white);
  font-size: 0.875rem;
  box-shadow: none;
}

.decision-form-dialog .decision-form-dialog__select.p-focus,
.decision-form-dialog .decision-form-dialog__select-wrap .p-select.p-focus {
  border-color: var(--color-orange);
  box-shadow: 0 0 0 2px rgba(228, 130, 26, 0.15);
}

.decision-form-dialog .decision-form-dialog__select .p-select-label,
.decision-form-dialog .decision-form-dialog__select-wrap .p-select-label {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: var(--color-dark);
}

.decision-form-dialog .p-dialog-mask {
  background: rgba(0, 0, 0, 0.4);
}
</style>
